import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { storageKeys } from '@/config/app';
import { local } from '@/lib/storage';
import { locales, translatorFor, type LocaleCode, type Translator } from './dictionary';
import { readStoredLang, setCurrentLang } from './i18n';

interface LocaleContextValue extends Translator {
  locales: typeof locales;
  setLang: (lang: LocaleCode) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

/**
 * Язык интерфейса: `lang` на <html> (RU переключает display-шрифт на Onest через токены),
 * выбор сохраняется в localStorage, по умолчанию английский (DEFAULT_LOCALE).
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<LocaleCode>(() => readStoredLang());

  useEffect(() => {
    document.documentElement.setAttribute('lang', lang);
    setCurrentLang(lang);
  }, [lang]);

  const setLang = useCallback((next: LocaleCode) => {
    local.set(storageKeys.lang, next);
    setLangState(next);
  }, []);

  const value = useMemo(() => ({ ...translatorFor(lang), locales, setLang }), [lang, setLang]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (!value) throw new Error('useLocale must be used inside <LocaleProvider>');
  return value;
}

/** `t(key, vars)` — строка; `tp(key, n, vars)` — форма множественного числа. */
export function useT(): Translator['t'] {
  return useLocale().t;
}

export function useTranslator(): Translator {
  const { lang, t, tp } = useLocale();
  return useMemo(() => ({ lang, t, tp }), [lang, t, tp]);
}
